import { useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type ImageMediaType = 'image' | 'gif';

export interface ImageUploadResult {
  url: string;
  type: ImageMediaType;
}

interface ImageUploadProps {
  onUpload: (result: ImageUploadResult) => void;
  onError: (message: string) => void;
  onLoadingChange: (loading: boolean) => void;
  children: (trigger: () => void) => React.ReactNode;
}

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

export const ImageUpload = ({ onUpload, onError, onLoadingChange, children }: ImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const trigger = () => inputRef.current?.click();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > MAX_IMAGE_SIZE) {
      onError('Imagem muito grande. Máximo 20MB.');
      return;
    }

    const isGif = file.type === 'image/gif';
    const ext = file.name.split('.').pop() ?? (isGif ? 'gif' : 'jpg');
    const path = `${user.id}/${Date.now()}.${ext}`;

    try {
      onLoadingChange(true);
      const { error } = await supabase.storage
        .from('post-media')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data } = supabase.storage.from('post-media').getPublicUrl(path);
      onUpload({ url: data.publicUrl, type: isGif ? 'gif' : 'image' });
    } catch {
      onError('Erro ao fazer upload da imagem. Tente novamente.');
    } finally {
      onLoadingChange(false);
      // Reset input so the same file can be re-selected after removal
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
      {children(trigger)}
    </>
  );
};
